import { useCallback, useMemo, useSyncExternalStore } from 'react';
import {
  parsePref,
  prefKey,
  readRawPref,
  subscribePrefKey,
  writeRawPref,
  type Pref,
  type PrefKeyArgs,
} from '@/lib/ui-prefs';

export type SetPref<T> = (next: T | ((current: T) => T)) => void;

/**
 * A registered UI pref (lib/ui-prefs.ts) as state: `[value, setValue]`.
 *
 * Bound to the key its placeholders make: a team pref is read for the team passed in, and its
 * setter writes only that team's key, so switching team shows the new team's value with no effect
 * and nothing lands under the wrong team. While a placeholder is missing (no team yet) the value is
 * the default and setting it does nothing. Other components and other tabs using the same key
 * follow along. Reads and writes are guarded; a write storage refuses still shows until a reload.
 *
 * ```ts
 * const [hidden, setHidden] = usePref(UI_PREFS.inboxNeedsReplyHidden, { teamId });
 * const [showChanges, setShowChanges] = usePref(UI_PREFS.tasksShowChanges);
 * ```
 */
export function usePref<T, K extends string>(pref: Pref<T, K>, ...args: PrefKeyArgs<K>): [T, SetPref<T>] {
  return usePrefAtKey(pref, prefKey(pref, ...args));
}

/**
 * `usePref` for a key already built, when callers pass whole keys around (a section's
 * `storageKey`). The key must belong to a registered family, with a line in ui-prefs.test.ts
 * when the module builds it. Null reads the default and writes nothing.
 */
export function usePrefAtKey<T>(pref: Pref<T>, key: string | null): [T, SetPref<T>] {
  const kind = pref.storage;
  const subscribe = useCallback((listener: () => void) => subscribePrefKey(kind, key, listener), [kind, key]);
  const getRaw = () => readRawPref(kind, key);
  // The raw text is the snapshot: a string compares by value, so an unchanged key never re-renders.
  const raw = useSyncExternalStore(subscribe, getRaw, getRaw);
  const value = useMemo(() => parsePref(pref, raw), [pref, raw]);
  const setValue = useCallback<SetPref<T>>(
    (next) => {
      if (!key) return;
      const current = parsePref(pref, readRawPref(kind, key));
      const value = typeof next === 'function' ? (next as (current: T) => T)(current) : next;
      writeRawPref(kind, key, pref.serialize(value));
    },
    [pref, kind, key]
  );
  return [value, setValue];
}
